/**
 * render_display_elements/display_input_elements.jsx
 *
 * 涵蓋所有文字/數字輸入類型的展示元件：
 * title, text-content, number_input, email_input, phone_input,
 * url_input, password_input, textarea, textarea_ocr, textarea_vernacular,
 * datetime, datetime_picker
 */

import React from "react"
import { Input } from "../../ui/input"
import { Button } from "../../ui/button"
import { FieldWrapper, ElementShell, TitleShell } from "../canvas_field_wrapper"

/** 共用：取得欄位標籤 */
const getLabel = (col) => col?.alt ?? col?.code ?? ""

/** title */
export const TitleElement = ({
    element,
    form_form_column_data_first,
    isEditing,
    selectedElement,
    editingContent,
    setEditingContent,
    handleFinishEditing,
    setEditingElementId,
    mode,
}) => {
    const value = element?.content ?? form_form_column_data_first?.default_value ?? ""
    return (
        <TitleShell justifyContent={element?.style?.justifyContent || "flex-start"}>
            <div className="w-full">
                {isEditing && selectedElement?.id === element?.id ? (
                    <Input
                        autoFocus
                        className="w-full"
                        value={editingContent ?? value}
                        onChange={(e) => setEditingContent(e.target.value)}
                        onBlur={handleFinishEditing}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); handleFinishEditing() }
                        }}
                    />
                ) : (
                    <div
                        className="w-full break-words"
                        style={{
                            color: element?.style?.textColor || "inherit",
                            fontSize: element?.style?.fontSize || 16,
                            fontFamily: element?.style?.fontFamily || "inherit",
                            fontWeight: element?.style?.fontWeight || "bold",
                            letterSpacing: element?.style?.letterSpacing ? `${element.style.letterSpacing}px` : "normal",
                            lineHeight: 1.3,
                        }}
                        onDoubleClick={() => {
                            if (mode === "design") {
                                setEditingElementId(element?.id)
                                setEditingContent(value)
                            }
                        }}
                    >
                        {value || "標題文字"}
                    </div>
                )}
                {form_form_column_data_first?.help_text && (
                    <p className="text-xs text-gray-400 mt-1">{form_form_column_data_first.help_text}</p>
                )}
            </div>
        </TitleShell>
    )
}

/** text-content (單行輸入) */
export const TextContentElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={getLabel(form_form_column_data_first)}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)

/** number_input */
export const NumberInputElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                type="number"
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                max={form_form_column_data_first?.max_value}
                min={form_form_column_data_first?.min_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)

/** email_input */
export const EmailInputElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                type="email"
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)

/** phone_input */
export const PhoneInputElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                type="tel"
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)

/** url_input */
export const UrlInputElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                type="url"
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)

/** password_input */
export const PasswordInputElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                type="password"
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)

/** textarea (共用邏輯) */
const TextareaBase = ({ form_form_column_data_first, isFillMode, topSlot }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={getLabel(form_form_column_data_first)}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            {topSlot}
            <Input.TextArea
                className="w-full max-h-[320px] overflow-y-auto resize-none"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
                rows={3}
            />
        </FieldWrapper>
    </ElementShell>
)

export const TextareaElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <TextareaBase form_form_column_data_first={form_form_column_data_first} isFillMode={isFillMode} />
)

export const TextareaOcrElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <TextareaBase
        form_form_column_data_first={form_form_column_data_first}
        isFillMode={isFillMode}
        topSlot={
            <Button size="sm" className="mb-2 bg-blue-600 hover:bg-blue-700 text-white self-start">
                文字辨識
            </Button>
        }
    />
)

export const TextareaVernacularElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <TextareaBase
        form_form_column_data_first={form_form_column_data_first}
        isFillMode={isFillMode}
        topSlot={
            <Button size="sm" className="mb-2 bg-blue-600 hover:bg-blue-700 text-white self-start">
                白話文解釋
            </Button>
        }
    />
)

/** datetime */
export const DatetimeElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                type="date"
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)

/** datetime_picker */
export const DatetimePickerElement = ({ element, form_form_column_data_first, isFillMode }) => (
    <ElementShell isFillMode={isFillMode}>
        <FieldWrapper
            isFillMode={isFillMode}
            label={form_form_column_data_first?.code}
            required={form_form_column_data_first?.is_required === "1"}
            helpText={form_form_column_data_first?.help_text}
        >
            <Input
                type="datetime-local"
                className="w-full"
                placeholder={form_form_column_data_first?.placeholder}
                defaultValue={form_form_column_data_first?.default_value}
                readOnly
            />
        </FieldWrapper>
    </ElementShell>
)
